import { z } from 'zod'

export const QuestionTypeSchema = z.enum([
  'SHORT_TEXT',
  'LONG_TEXT',
  'MULTIPLE_CHOICE',
  'DROPDOWN',
  'CHECKBOXES',
  'PICTURE_CHOICE',
  'YES_NO',
  'OPINION_SCALE',
  'RATING',
  'NPS',
  'DATE',
  'TIME',
  'PHONE_NUMBER',
  'TAIWAN_MOBILE',
  'TAIWAN_LANDLINE',
  'TAIWAN_ADDRESS',
  'EMAIL',
  'WEBSITE',
  'NUMBER',
  'NUMBER_SLIDER',
  'TOTAL',
  'NESTED_SELECT',
  'FILE_UPLOAD',
  'SIGNATURE',
  'RANKING',
  'MATRIX',
  'STATEMENT',
  'SECTION_TITLE',
  'INTRO_BLOCK',
  'DIVIDER',
])

export type QuestionType = z.infer<typeof QuestionTypeSchema>

export const LayoutQuestionTypeSchema = z.enum([
  'STATEMENT',
  'SECTION_TITLE',
  'INTRO_BLOCK',
  'DIVIDER',
])

export type LayoutQuestionType = z.infer<typeof LayoutQuestionTypeSchema>

export function isLayoutQuestionType(type: QuestionType): type is LayoutQuestionType {
  return LayoutQuestionTypeSchema.safeParse(type).success
}

export type FormStatus = 'draft' | 'published' | 'closed' | 'archived'

export type Choice = {
  id: string
  questionId: string
  label: string
  value: string
  score?: number
  amount?: number
  imageUrl?: string
  imageAlt?: string
  imageFit?: 'cover' | 'contain'
  imageFocalX?: number
  imageFocalY?: number
  imageZoom?: number
  imageRadius?: number
  orderIndex: number
}

export type FormTheme = {
  backgroundColor: string
  textColor: string
  answerColor: string
  buttonColor: string
  buttonTextColor: string
  accentColor: string
  radius: number
  maxWidth: number
  fontFamily: string
  headingFontFamily?: string
  bodyFontFamily?: string
  fontScale?: 'compact' | 'comfortable' | 'large'
  lineHeight?: number
  progressStyle: 'thin' | 'thick' | 'text'
}

export type Form = {
  id: string
  workspaceId: string
  title: string
  description: string
  status: FormStatus
  themeConfig: FormTheme
  settings: Record<string, unknown>
  welcomeScreen: {
    enabled?: boolean
    title: string
    description: string
    coverUrl?: string
    coverAlt?: string
    coverFit?: 'cover' | 'contain'
    coverFocalX?: number
    coverFocalY?: number
    coverZoom?: number
    coverRadius?: number
    coverHeight?: number
  }
  thankYouScreen: {
    title: string
    description: string
    animationStyle?: 'check_confetti' | 'spotlight' | 'card_stack' | 'minimal'
    primaryCtaLabel?: string
    primaryCtaUrl?: string
    showPrimaryCta?: boolean
    secondaryCtaLabel?: string
    secondaryCtaUrl?: string
    showSecondaryCta?: boolean
    redirectUrl?: string
    redirectDelaySeconds?: number
    showResubmitButton?: boolean
    showShareButtons?: boolean
    showNewSubmissionButton?: boolean
    newSubmissionButtonLabel?: string
  }
  createdBy: string
  updatedAt: string
}

export type NestedSelectOption = {
  id: string
  label: string
  value: string
  children?: NestedSelectOption[]
}

export type ConditionalDisplayMode =
  | 'show_when_equals'
  | 'show_when_answered'
  | 'show_when_not_answered'

export type ConditionalDisplayCondition = {
  controllerQuestionId: string
  sourceControllerQuestionId?: string
  controllerValue?: string
  mode?: ConditionalDisplayMode
  source?: string
}

export type QuestionSettings = {
  buttonText?: string
  errorMessage?: string
  showQuestionNumber?: boolean
  placeholder?: string
  minLength?: number
  maxLength?: number
  validation?: 'none' | 'email' | 'phone' | 'url' | 'number'
  validationPreset?: string
  validationRegex?: string
  validationTestValue?: string
  taiwanLandlineAreaCode?: string
  addressMode?: 'taiwan_city_district_postal_detail'
  postalCodePrecision?: 'district_3digit_padded_5'
  detailPlaceholder?: string
  addressDefaultSourceMode?: string
  addressSourceOptions?: Array<{
    value: string
    label: string
    sourceQuestionId?: string
    sourceQuestionTitle?: string
  }>
  choiceValueMode?: 'auto' | 'manual'
  dependsOnQuestionId?: string
  sourceDependsOnQuestionId?: string
  dependentSource?: string
  dependentChoicesByQuestionValue?: Record<string, string[]>
  dependentChoiceEmptyMessage?: string
  d1QuestionNo?: number
  sharedDbFieldName?: string
  splitFromQuestionId?: string
  splitControllerQuestionId?: string
  splitControllerValue?: string
  conditionalDisplay?: ConditionalDisplayCondition & {
    conditions?: ConditionalDisplayCondition[]
    conditionGroups?: ConditionalDisplayCondition[][]
    operator?: 'AND' | 'OR'
  }
  readOnly?: boolean
  readOnlyReason?: string
  autofillSourceQuestionId?: string
  sourceAutofillSourceQuestionId?: string
  existingCustomerNameTargetQuestionId?: string
  sourceExistingCustomerNameTargetQuestionId?: string
  customerShortNameSource?: 'existing_customer_lookup'
  exportValueType?: 'text'
  preserveLeadingZeros?: boolean
  taxIdLookup?: {
    enabled: boolean
    providerMode: 'synthetic' | 'gcis_open_data'
    sourceLabel: string
    targetQuestionId: string
    sourceTargetQuestionId?: string
  }
  customerDuplicateCheck?: {
    enabled: boolean
    providerMode: 'webhook'
    field: 'customer_code' | 'customer_short_name' | 'existing_customer_code'
    purpose?: 'duplicate_check' | 'existing_lookup'
    sourceLabel: string
  }
  enableChoiceScoring?: boolean
  defaultChoiceScore?: number
  defaultChoiceAmount?: number
  allowMultiple?: boolean
  showOther?: boolean
  autoAdvance?: boolean
  defaultValue?: AnswerValue
  randomize?: boolean
  minSelected?: number
  maxSelected?: number
  steps?: number
  style?: 'number' | 'stars' | 'hearts' | 'emoji'
  startLabel?: string
  endLabel?: string
  allowedTypes?: string[]
  maxFileSizeMb?: number
  maxFiles?: number
  timeMin?: string
  timeMax?: string
  timeStepMinutes?: number
  rows?: string[]
  columns?: string[]
  selectionMode?: 'single' | 'multiple'
  numberMin?: number
  numberMax?: number
  numberStep?: number
  numberUnit?: string
  numberDecimals?: number
  numberUseThousands?: boolean
  numberPrefix?: string
  numberSuffix?: string
  numberErrorMessage?: string
  sliderMin?: number
  sliderMax?: number
  sliderStep?: number
  sliderShowValue?: boolean
  sliderShowTicks?: boolean
  sliderTickLabels?: string[]
  totalMode?: 'sum' | 'average' | 'count' | 'custom'
  totalSourceQuestionIds?: string[]
  totalPreviewValue?: number
  totalUnit?: string
  totalPrefix?: string
  totalSuffix?: string
  totalFormula?: string
  nestedLevels?: string[]
  nestedOptions?: NestedSelectOption[]
  layoutTone?: 'neutral' | 'accent' | 'success' | 'warning'
  layoutAlignment?: 'left' | 'center' | 'right'
  dividerStyle?: 'solid' | 'dashed' | 'dotted'
  dividerLabel?: string
  calloutLabel?: string
  [key: string]: unknown
}

export type Question = {
  id: string
  formId: string
  groupId?: string
  type: QuestionType
  title: string
  description?: string
  required: boolean
  settings: QuestionSettings
  choices: Choice[]
  orderIndex: number
}

export type QuestionGroupReference = {
  id: string
  title?: string
  questionIds: string[]
  questions?: Question[]
}

export type AnswerValue =
  | string
  | number
  | boolean
  | string[]
  | Record<string, string | number | boolean | string[] | null>
  | null

export type PublicForm = Form & {
  questionGroups?: QuestionGroupReference[]
  questions: Question[]
  logicJumps: LogicJump[]
  hiddenFields: Record<string, string>
}

export type ResponseSummary = {
  id: string
  formId: string
  status: 'complete' | 'partial'
  archiveStatus?: 'active' | 'archived'
  archivedAt?: string
  submittedAt: string
  completedAt?: string
  timeSpentSeconds: number
  answers: Record<string, AnswerValue>
  customer?: {
    companyName?: string
    customerCode?: string
    contactName?: string
    contactTitle?: string
    contactEmail?: string
    contactPhone?: string
    ownerName?: string
  }
  metadata?: Record<string, string | number | boolean | null>
}

export type DashboardStats = {
  totalForms: number
  monthlyResponses: number
  averageCompletionRate: number
  averageTimeSeconds: number
  responseTrend: Array<{ date: string; responses: number; starts: number; impressions: number }>
  activeForms: Array<{
    id: string
    title: string
    responses: number
    completionRate: number
    updatedAt: string
  }>
  latestResponses: Array<{ id: string; formTitle: string; preview: string; submittedAt: string }>
}

export type LogicConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'ends_with'
  | 'greater_than'
  | 'less_than'
  | 'greater_or_equal'
  | 'less_or_equal'
  | 'is_answered'
  | 'is_not_answered'
  | 'includes'
  | 'not_includes'
  | 'between'

export type LogicCondition = {
  field: string
  operator: LogicConditionOperator
  value?: string | number | string[]
}

export type LogicDisplayTarget =
  | {
      type: 'question'
      id: string
      questionId: string
      groupId?: string | null
    }
  | {
      type: 'group'
      id: string
      groupId: string
    }
  | {
      type: 'group_question'
      id: string
      groupId: string
      questionId: string
    }

export type LogicJump = {
  id: string
  questionId: string
  conditions: LogicCondition[]
  operator: 'AND' | 'OR'
  targetQuestionId: string | null
  targetGroupId?: string | null
  displayTargets?: LogicDisplayTarget[]
  order: number
}
